import { useState, useEffect, forwardRef } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PhoneInput, { type Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { auth } from '@/lib/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';

const CustomPhoneInput = forwardRef<HTMLInputElement, any>((props, ref) => {
  return <Input {...props} ref={ref} />;
});
CustomPhoneInput.displayName = 'CustomPhoneInput';

const PhoneSignIn: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [country, setCountry] = useState<Country>('IN');
  const [otp, setOtp] = useState<string>('');
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (typeof window !== 'undefined' && auth) {
      (window as any).recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: (response: any) => {
          console.log('reCAPTCHA solved');
        },
      });
    }
  }, []);

  const handleSendOtp = async () => {
    if (!phoneNumber) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const appVerifier = (window as any).recaptchaVerifier;
      const result = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(result);
    } catch (error) {
      console.error('Error sending OTP:', error);
      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setError('Please enter the OTP');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await confirmationResult.confirm(otp);
      router.push('/'); // Redirect to the home page on successful login
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError('Invalid OTP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    setConfirmationResult(null);
    setError(null);
    setOtp('');
    setPhoneNumber('');
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
      <div id="recaptcha-container"></div>
      <h2 className="text-2xl font-bold text-center mb-6">Login</h2>
      {!confirmationResult ? (
        <form onSubmit={(e) => { e.preventDefault(); handleSendOtp(); }}>
          <div className="space-y-4">
            <Label htmlFor="phone-number">Phone Number</Label>
            <PhoneInput
              id="phone-number"
              value={phoneNumber}
              onChange={(value) => setPhoneNumber(value || '')}
              placeholder="Enter your phone number"
              country={country}
              defaultCountry="IN"
              onCountryChange={(c) => c && setCountry(c)}
              inputComponent={CustomPhoneInput}
            />
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Sending OTP...' : 'Send OTP'}
            </Button>
            <Button type="button" variant="link" onClick={() => router.push('/')} className="w-full">
                Go to Home Page
            </Button>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }}>
          <div className="space-y-4">
            <Label htmlFor="otp">Enter OTP</Label>
            <Input
              id="otp"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter the 6-digit OTP"
            />
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Verifying...' : 'Verify OTP'}
            </Button>
            <Button type="button" variant="link" onClick={handleGoBack} className="w-full">
                Go Back
            </Button>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
        </form>
      )}
    </div>
  );
};

export default PhoneSignIn;